import { parse } from "date-fns";
import type { AutoCombineEntryV2, EntryMiniTransaction } from "@/shared/cache";
import { Badge } from "@/v2/components/ui/badge";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { AppIcon } from "@/v2/lib/appIcons";
import { cn } from "@/v2/lib/utils";

function formatTime(t: string | null): string | null {
	if (!t) return null;
	const [hStr, mStr] = t.split(":");
	const h = Number(hStr);
	const m = Number(mStr);
	if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
	return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function formatShortDate(iso: string): string {
	if (!iso) return "";
	const d = parse(iso, "yyyy-MM-dd", new Date());
	if (Number.isNaN(d.getTime())) return iso;
	const month = d.toLocaleString(undefined, { month: "short" });
	return `${month} ${d.getDate()}`;
}

function sourceNameFor(mini: EntryMiniTransaction): string | null {
	return mini.canonicalName?.trim() || null;
}

interface MiniRowProps {
	mini: EntryMiniTransaction;
	parentDate: string;
	selected: boolean;
	onToggle: (id: string) => void;
	onEdit: (mini: EntryMiniTransaction) => void;
	selectable: boolean;
	markerColumn: string;
	checkboxColumn: string;
	contentColumn: string;
	unitsColumn: string | null;
}

function MiniRow({
	mini,
	parentDate,
	selected,
	onToggle,
	onEdit,
	selectable,
	markerColumn,
	checkboxColumn,
	contentColumn,
	unitsColumn,
}: MiniRowProps) {
	const time = formatTime(mini.time);
	const showDate = mini.date !== parentDate;
	const sourceName = sourceNameFor(mini);
	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled via the checkbox control.
		// biome-ignore lint/a11y/noStaticElementInteractions: row is a convenience click target; the checkbox is the accessible control.
		<div
			data-state={selected ? "selected" : undefined}
			className={cn(
				"group/mini grid cursor-pointer items-stretch pl-6 transition-colors",
				"data-[state=selected]:bg-[color-mix(in_oklch,var(--brand-blue)_9%,transparent)]",
				"[&:has([data-mini-content]:hover)>[data-mini-content]]:bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))]",
				selectable &&
					"[&:has([data-mini-content]:hover)[data-state=selected]>[data-mini-content]]:bg-[color-mix(in_oklch,var(--brand-blue)_13%,transparent)]",
			)}
			style={{ gridTemplateColumns: "var(--auto-cols)" }}
			onClick={(e) => {
				if (!selectable) return;
				if ((e.target as HTMLElement).closest("[data-mini-checkbox]")) return;
				onEdit(mini);
			}}
		>
			<div
				className="flex h-full items-center justify-end pr-1.5"
				style={{ gridColumn: markerColumn }}
			>
				{mini.isNewSinceNarrativeGeneration ? (
					<span
						className="size-2 shrink-0 rounded-full bg-[var(--brand-blue)] shadow-[0_0_0_2px_color-mix(in_oklch,var(--brand-blue)_18%,transparent)]"
						title="New or updated since the narrative was generated"
						role="img"
						aria-label="New or updated since the narrative was generated"
					/>
				) : null}
			</div>
			{/* Full-height touch target. The row itself is indented so source-entry
			    checkboxes don't line up with entry-group checkboxes, preserving the
			    group → source hierarchy without adding margins or rounded corners. */}
			<button
				type="button"
				className="flex h-full w-full cursor-pointer items-center justify-center justify-self-stretch rounded-none transition-colors hover:bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] disabled:cursor-default disabled:hover:bg-transparent"
				style={{ gridColumn: checkboxColumn }}
				data-mini-checkbox
				disabled={!selectable}
				onClick={(e) => {
					e.stopPropagation();
					if (selectable) onToggle(mini.id);
				}}
				aria-label="Select source entry"
			>
				<Checkbox
					checked={selected}
					disabled={!selectable}
					tabIndex={-1}
					className="pointer-events-none scale-[0.8]"
				/>
			</button>
			<div
				data-mini-content
				className="flex min-w-0 items-center gap-3 py-2 pl-2.5 pr-3 transition-colors"
				style={{ gridColumn: contentColumn }}
			>
				<AppIcon
					application={mini.application}
					className="size-4 shrink-0 text-muted-foreground"
				/>
				<span className="w-[4.5rem] shrink-0 text-xs tabular-nums text-muted-foreground">
					{showDate ? `${formatShortDate(mini.date)} ` : ""}
					{time ?? ""}
				</span>
				<div className="flex min-w-0 flex-col gap-0.5">
					{sourceName && (
						<span className="min-w-0 text-[0.8125rem] text-foreground/85">
							{sourceName}
						</span>
					)}
					{mini.summary && (
						<span className="min-w-0 text-[0.8125rem] text-muted-foreground">
							{mini.summary}
						</span>
					)}
				</div>
			</div>
			{unitsColumn && (
				<div
					data-mini-content
					className="flex items-center justify-center py-2 transition-colors"
					style={{ gridColumn: unitsColumn }}
				>
					<Badge className="min-w-[44px] justify-center rounded-[6px] border border-border bg-transparent px-2 py-1 text-xs font-medium leading-none tabular-nums text-muted-foreground">
						{mini.units.toFixed(1)}
					</Badge>
				</div>
			)}
		</div>
	);
}

interface AutoCombineMiniTransactionsProps {
	entry: AutoCombineEntryV2;
	visibleColumnIds: string[];
	selectedMiniIds: Set<string>;
	onToggleMini: (id: string) => void;
	onEditMini: (entry: AutoCombineEntryV2, mini: EntryMiniTransaction) => void;
	pendingIds?: Set<string>;
}

/**
 * Source-entry detail for an entry group. Rows align to the entry group's
 * column grid (checkbox and units line up), carry no status — the group owns
 * it — and are read-only when the group is terminal
 * (exported/deleted), since those can't be regrouped.
 */
export function AutoCombineMiniTransactions({
	entry,
	visibleColumnIds,
	selectedMiniIds,
	onToggleMini,
	onEditMini,
	pendingIds = new Set(),
}: AutoCombineMiniTransactionsProps) {
	const selectable = entry.status !== "exported" && entry.status !== "deleted";

	// Grid lines: track 1 is the expand gutter, track 2 is `_select`, so a leaf
	// column at visible index `i` occupies grid line `i + 2`. Aligning the source
	// checkbox + units to those exact lines keeps them flush with the group row.
	const checkboxTrack = visibleColumnIds.indexOf("_select") + 2;
	const unitsIdx = visibleColumnIds.indexOf("units");
	const unitsTrack = unitsIdx >= 0 ? unitsIdx + 2 : null;
	const markerColumn = `${checkboxTrack - 1} / ${checkboxTrack}`;
	const checkboxColumn = `${checkboxTrack} / ${checkboxTrack + 1}`;
	const contentColumn = `${checkboxTrack + 1} / ${unitsTrack ?? -2}`;
	const unitsColumn = unitsTrack ? `${unitsTrack} / ${unitsTrack + 1}` : null;

	return (
		<div className="border-t border-border/60 bg-muted divide-y divide-foreground/[0.05]">
			{entry.miniTransactions.map((mini) => (
				<MiniRow
					key={mini.id}
					mini={mini}
					parentDate={entry.date}
					selected={selectedMiniIds.has(mini.id)}
					onToggle={onToggleMini}
					onEdit={(mini) => onEditMini(entry, mini)}
					selectable={
						selectable && !pendingIds.has(entry.id) && !pendingIds.has(mini.id)
					}
					markerColumn={markerColumn}
					checkboxColumn={checkboxColumn}
					contentColumn={contentColumn}
					unitsColumn={unitsColumn}
				/>
			))}
		</div>
	);
}
